'use client';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { applyDictationTranscript } from '#pplx/frontend/composer-sdk-plugins/dictation/applyDictationTranscript.js';
import { useComposerEditor } from '#pplx/frontend/composer-sdk/ComposerEditorContext.js';
import { useComposer } from '#pplx/frontend/composer-sdk/useComposer.js';
import { useEffect, useRef } from 'react';
export function DictationTranscriptSync({ focusRequest, isTranscribing, onStopTranscription, stopOnSubmitGesture = true, transcription, }) {
    const { isDisabled } = useComposer();
    const { append, focus, isReady, replaceSuffixOrAppend, subscribeUserTakeover } = useComposerEditor();
    const activeSessionRef = useRef(null);
    const pendingSessionsRef = useRef([]);
    const handledFocusRequestRef = useRef(focusRequest);
    useEffect(() => {
        if (focusRequest === handledFocusRequestRef.current)
            return;
        handledFocusRequestRef.current = focusRequest;
        focus();
    }, [focus, focusRequest]);
    useEffect(() => {
        if (isTranscribing) {
            const session = activeSessionRef.current ?? {
                appliedTranscript: '',
                transcription: '',
                isDiscarded: false,
            };
            if (activeSessionRef.current === null) {
                activeSessionRef.current = session;
                pendingSessionsRef.current.push(session);
            }
            // Empty interim results must not discard text the editor has not accepted yet.
            if (transcription && !session.isDiscarded)
                session.transcription = transcription;
        }
        else {
            activeSessionRef.current = null;
        }
        // Stopped recordings stay in order until accepted; the active session stays last
        // so future cumulative corrections can still reconcile against its applied prefix.
        let completedSessions = 0;
        for (const session of pendingSessionsRef.current) {
            if (session !== activeSessionRef.current &&
                !session.appliedTranscript &&
                !session.transcription.trim()) {
                completedSessions += 1;
                continue;
            }
            session.appliedTranscript = applyDictationTranscript({
                adapter: isDisabled || !isReady
                    ? null
                    : {
                        append: (delta, isInitial) => append(isInitial ? delta.trimStart() : delta, {
                            prefixWhenNonEmpty: isInitial ? ' ' : '',
                            trimExistingContent: isInitial,
                        }),
                        replace: (text, suffix) => replaceSuffixOrAppend({ prefixWhenNonEmpty: ' ', suffix, text }),
                    },
                appliedTranscript: session.appliedTranscript,
                // A queued session still reconciles after microphone recording stops.
                isTranscribing: true,
                transcription: session.transcription,
            });
            if (session === activeSessionRef.current ||
                session.appliedTranscript !== session.transcription) {
                break;
            }
            completedSessions += 1;
        }
        pendingSessionsRef.current.splice(0, completedSessions);
    }, [append, isDisabled, isReady, isTranscribing, replaceSuffixOrAppend, transcription]);
    useEffect(() => {
        return subscribeUserTakeover((reason) => {
            switch (reason) {
                case 'edit':
                case 'reset':
                    break;
                case 'submit':
                    if (!stopOnSubmitGesture)
                        return;
                    break;
                default:
                    assertUnreachableValue(reason);
            }
            pendingSessionsRef.current = [];
            // A delayed stop notification must not let the same recording repopulate the queue.
            if (activeSessionRef.current !== null)
                activeSessionRef.current.isDiscarded = true;
            if (isTranscribing)
                onStopTranscription();
        });
    }, [isTranscribing, onStopTranscription, stopOnSubmitGesture, subscribeUserTakeover]);
    return null;
}
//# sourceMappingURL=DictationTranscriptSync.js.map